import React, { Fragment, Suspense, useEffect, useState } from 'react';
import { NetworkStatus, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  FormControl,
  Icon,
  Intrinsic,
  Select,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToastAction,
  ToastType,
  useToastsDispatch,
  theme,
} from '@bechtel/gds';
import {
  AppDataFormat,
  AppDataFormatEnum,
  AppTimeZone,
  Attachment,
  AttachmentMetadataInput,
  ProjectLocationInput,
  Country,
  Language,
  Project,
  ProjectSupportUser,
  ProjectSupportUserInput,
  State,
  UpdateProjectInput,
  UpdateProjectPayload,
} from '@bechtel/types';
import { ErrorMessage, Field, Formik } from 'formik';
import { get } from 'lodash';
import { object, array, string, lazy as yupLazy, mixed } from 'yup';
import {
  FileUpload,
  FileUploadInput,
  FileUploadPreviewImg,
  FileUploadResponse,
  ErrorBoundary,
  ErrorRetry,
  useAppUser,
  SuspenseLoader,
  useEnvProvider,
  ProjectAppTypes,
  useConfig,
} from '@bechtel/client-common';
import {
  GET_COUNTRIES,
  GET_LANGUAGES,
  GET_PROJECT_BY_IDS,
  GET_STATES,
  GET_TIMEZONES,
  UPDATE_PROJECT,
} from '@bechtel/client-gql';
import { useAppDataFormats } from '../hooks/use-app-data-format';
import {
  DateFormatItem,
  DateFormatSelectedItem,
  DisplayFileDetails,
  DropdownItem,
  DropdownSelectedItem,
  FieldError,
  TimeFormatItem,
  TimeFormatSelectedItem,
  TimeZoneItem,
  TimeZoneSelectedItem,
} from './common';
import { UpdateProjectRequestError } from './error/types';
import {
  DeletePhotoStyled,
  FooterButtonStyled,
  GeneralContainerStyled,
  GeneralFooterButtonStyled,
  GeneralHeaderStyled,
  GeneralInnerBlockStyled,
  GeneralSubContainerStyled,
  ImagePreviewContainerStyled,
  PhotoContainerStyled,
  TextAreaStyled,
  TextInputStyled,
} from './styled';
import { Map } from '@bechtel/client-weld-common';
import UserItem from './user-item';
import UpdateProjectRequestErrorModal from './error';
import LogoDeleteModal from '~/general/confirm-delete';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

export interface FormikState {
  projectNumber: string;
  projectName: string;
  language: Language;
  country: Country;
  state?: State | null | undefined;
  city: string;
  minLatitudeField: number | null | undefined;
  maxLatitudeField: number | null | undefined;
  minLongitudeField: number | null | undefined;
  maxLongitudeField: number | null | undefined;
  dateFormat: AppDataFormat;
  timeFormat: AppDataFormat;
  projectSupportInfo?: string;
  projectLogoDisplay?: Attachment;
  projectLogoInput?: AttachmentMetadataInput;
  timezone: AppTimeZone;
  projectSupportUsers: ProjectSupportUser[];
  isoDocumentPrefix: string;
}

let initialValues: FormikState | undefined = undefined;
const phoneRegExp = /^((\\+[1-9]{1,12}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
const isoDocumentRegExp = /^(\d{5},)*\d{5}$/;
const projectDetailsValidationSchema = object().shape(
  {
    projectName: string()
      .trim()
      .min(1, t`Please enter atleast one character for project name`)
      .max(255, t`Please limit project name to 255 characters`)
      .required(t`Project name is mandatory`),
    country: yupLazy((value) => {
      if (value) {
        return object().shape({
          id: string().required(t`Country is mandatory`),
        });
      }
      return mixed().required(t`Country is mandatory`);
    }),
    state: yupLazy((value) => {
      if (value) {
        return object().shape({
          id: string().required(t`State is mandatory.`),
        });
      }
      return mixed().required(t`State is mandatory`);
    }),
    city: string().required(t`City is mandatory.`),
    minLatitudeField: string()
      .ensure()
      .when(['maxLatitudeField', 'minLongitudeField', 'maxLongitudeField'], {
        is: (maxLatitudeField, maxLongitudeField, minLongitudeField) =>
          maxLatitudeField != '' || maxLongitudeField != '' || minLongitudeField != '',
        then: string().required(),
        otherwise: string().notRequired(),
      }),
    maxLatitudeField: string()
      .ensure()
      .when(['minLatitudeField', 'maxLongitudeField', 'minLongitudeField'], {
        is: (minLatitudeField, maxLongitudeField, minLongitudeField) =>
          minLatitudeField != '' || maxLongitudeField != '' || minLongitudeField != '',
        then: string().required(),
        otherwise: string().notRequired(),
      }),
    minLongitudeField: string()
      .ensure()
      .when(['maxLatitudeField', 'minLatitudeField', 'maxLongitudeField'], {
        is: (minLatitudeField, maxLatitudeField, maxLongitudeField) =>
          minLatitudeField != '' || maxLatitudeField != '' || maxLongitudeField != '',
        then: string().required(),
        otherwise: string().notRequired(),
      }),
    maxLongitudeField: string()
      .ensure()
      .when(['minLatitudeField', 'maxLatitudeField', 'minLongitudeField'], {
        is: (minLatitudeField, maxLatitudeField, minLongitudeField) =>
          minLatitudeField != '' || maxLatitudeField != '' || minLongitudeField != '',
        then: string().required(),
        otherwise: string().notRequired(),
      }),

    dateFormat: yupLazy((value) => {
      if (value) {
        return object().shape({
          id: string().required(t`Date format is mandatory`),
        });
      }
      return mixed().required(t`Date format is mandatory`);
    }),
    timeFormat: yupLazy((value) => {
      if (value) {
        return object().shape({
          id: string().required(t`Time format is mandatory`),
        });
      }
      return mixed().required(t`Time format is mandatory`);
    }),
    timezone: yupLazy((value) => {
      if (value) {
        return object().shape({
          id: string().required(t`Time Zone format is mandatory`),
        });
      }
      return mixed().required(t`Time Zone format is mandatory`);
    }),
    projectSupportUsers: array().of(
      object().shape({
        email: string()
          .email('Invalid email format')
          .nullable(),
        phone: string()
          .matches(phoneRegExp, 'Phone number is not valid')
          .nullable(),
        location: string()
          .trim()
          .max(1999, t`Location address too large`)
          .nullable(),
      })
    ),
    isoDocumentPrefix: string()
      .matches(isoDocumentRegExp, t`Invalid ISO Document Prefix`)
      .nullable(),
  },
  [
    ['minLatitudeField', 'maxLatitudeField'],
    ['minLatitudeField', 'maxLongitudeField'],
    ['minLatitudeField', 'minLongitudeField'],
    ['maxLatitudeField', 'maxLongitudeField'],
    ['maxLatitudeField', 'minLongitudeField'],
    ['maxLongitudeField', 'minLongitudeField'],
  ]
);

const ProjectMapLocation = () => {
  const { envConfig } = useEnvProvider();
  const { user } = useAppUser();
  const projectLocationObj = user?.project?.projectConfiguration?.projectLocation;
  const maxLatitude = projectLocationObj?.maxLatitude;
  const maxLongitude = projectLocationObj?.maxLongitude;
  const minLatitude = projectLocationObj?.minLatitude;
  const minLongitude = projectLocationObj?.minLongitude;
  if (maxLatitude && maxLongitude && minLatitude && minLongitude) {
    const mapCenter: google.maps.LatLngLiteral = {
      lat: (minLatitude + maxLatitude) / 2,
      lng: (minLongitude + maxLongitude) / 2,
    };
    return (
      <Box flexDirection={{ sm: 'column' }} my={{ sm: '500' }} flexGrow={{ sm: '1' }} data-testid="projectMapLocation">
        <Box
          mt={{ sm: '550' }}
          style={{
            minHeight: '150px',
            width: '100%',
          }}
        >
          <Map apiKey={envConfig?.mapApiKey} zoom={16} center={mapCenter} markers={''} mapTypeId="satellite" />
        </Box>
      </Box>
    );
  }
};

export const ManageGeneralSettings = () => {
  const { user } = useAppUser();
  const { i18n } = useLingui();
  const { features } = useConfig();
  const { envConfig } = useEnvProvider();
  const dispatchToast = useToastsDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [country, setCountry] = useState<Country>();
  const [updateProjectRequestError, setUpdateProjectRequestError] = useState<UpdateProjectRequestError | null>();
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  const widthLatitude = '168px';

  const ProjectUpdateToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Project details saved successfully.</Trans>
        </Text>
      </Box>
    );
  };
  const onUpdateProjectSubmitComplete = (data: { updateProject: UpdateProjectPayload }) => {
    const updateProjectRequestError = get(data, 'updateProject.error');

    if (updateProjectRequestError) {
      setUpdateProjectRequestError(updateProjectRequestError);
    } else {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: ProjectUpdateToast,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
    }
  };

  const closeModal = () => {
    setUpdateProjectRequestError(null);
  };

  const [updateProject, { loading: updateProjectLoading }] = useMutation<
    { updateProject: UpdateProjectPayload },
    { input: UpdateProjectInput }
  >(UPDATE_PROJECT, {
    onCompleted: onUpdateProjectSubmitComplete,
    onError: (error) => {
      setUpdateProjectRequestError(error);
    },
  });

  const [
    getLanguages,
    {
      loading: lngLoading,
      error: lngError,
      data: lngData,
      refetch: lngRefetch,
      networkStatus: lngNetworkStatus,
      called: lngCalled,
    },
  ] = useLazyQuery<{ languages: Language }>(GET_LANGUAGES, {
    notifyOnNetworkStatusChange: true,
  });
  const languageList = get(lngData, 'languages');

  const [
    getTimeZones,
    {
      loading: timezoneLoading,
      error: timezoneError,
      data: timezoneData,
      refetch: timezoneRefetch,
      networkStatus: timezoneNetworkStatus,
      called: timezoneCalled,
    },
  ] = useLazyQuery<{ timezones: AppTimeZone }>(GET_TIMEZONES, {
    notifyOnNetworkStatusChange: true,
  });
  const timezoneList = get(timezoneData, 'timeZones');

  const [
    getCountries,
    {
      loading: cntLoading,
      error: cntError,
      data: cntData,
      refetch: cntRefetch,
      networkStatus: cntNetworkStatus,
      called: cntCalled,
    },
  ] = useLazyQuery<{ countries: Country }>(GET_COUNTRIES, {
    notifyOnNetworkStatusChange: true,
  });
  const countryList = get(cntData, 'countries');

  const [
    getAppDateFormats,
    {
      loading: dateFormatLoading,
      error: dateFormatError,
      data: dateFormatData,
      refetch: dateFormatRefetch,
      networkStatus: dateFormatNetworkStatus,
      called: dateFormatCalled,
    },
  ] = useAppDataFormats(true);

  const dateFormatList = get(dateFormatData, 'appDataFormats');

  const [
    getAppTimeFormats,
    {
      loading: timeFormatLoading,
      error: timeFormatError,
      data: timeFormatData,
      refetch: timeFormatRefetch,
      networkStatus: timeFormatNetworkStatus,
      called: timeFormatCalled,
    },
  ] = useAppDataFormats(true);

  const timeFormatList = get(timeFormatData, 'appDataFormats');

  const [
    getStates,
    { loading: stLoading, error: stError, data: stData, refetch: stRefetch, networkStatus: stNetworkStatus },
  ] = useLazyQuery<{ states: State }, { countryId: string }>(GET_STATES, {
    notifyOnNetworkStatusChange: true,
  });
  const stateList = get(stData, 'states');

  useEffect(() => {
    if (!country || !country.id) return;
    getStates({
      variables: {
        countryId: country.id,
      },
    });
  }, [country]);

  const {
    loading: projectLoading,
    error: projectError,
    data: projectData,
    refetch: projectRefetch,
    networkStatus: projectNetworkStatus,
  } = useQuery(GET_PROJECT_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ids: [user?.project?.id],
    },
  });
  const projectDetailArr: Project[] = get(projectData, 'projectsByIds', []);

  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;

  useEffect(() => {
    if (!projectDetail || !projectDetail.id) return;

    if (!lngCalled) getLanguages();
    if (!timezoneCalled) getTimeZones();
    if (!cntCalled) getCountries();

    if (!dateFormatCalled)
      getAppDateFormats({
        variables: {
          formatTypeId: AppDataFormatEnum.DATE_FORMAT,
        },
      });
    if (!timeFormatCalled)
      getAppTimeFormats({
        variables: {
          formatTypeId: AppDataFormatEnum.TIME_FORMAT,
        },
      });

    if (projectDetail && projectDetail.country) setCountry(projectDetail.country);
    let projectDateFormat, projectTimeFormat;
    if (
      projectDetail.projectConfiguration.appDataFormats &&
      projectDetail.projectConfiguration.appDataFormats.length > 0
    ) {
      projectDateFormat = projectDetail.projectConfiguration.appDataFormats.find(
        (inner) => inner.formatTypeId == AppDataFormatEnum.DATE_FORMAT
      );
      projectTimeFormat = projectDetail.projectConfiguration.appDataFormats.find(
        (inner) => inner.formatTypeId == AppDataFormatEnum.TIME_FORMAT
      );
    }

    let projectLogoInput: AttachmentMetadataInput | undefined = undefined;
    if (projectDetail.logoAttachment) {
      projectLogoInput = {
        attachmentId: projectDetail.logoAttachment.id,
        fileName: projectDetail.logoAttachment.fileName,
        fileSize: parseFloat(projectDetail.logoAttachment.fileSize),
        mimeType: projectDetail.logoAttachment.mimeType,
      };
    }

    const projectLocationInputs = projectDetail.projectConfiguration?.projectLocation;

    initialValues = {
      projectNumber: projectDetail.jobNumber,
      projectName: projectDetail.name,
      language: projectDetail.language,
      country: projectDetail.country,
      state: projectDetail.state,
      city: projectDetail.location,
      minLatitudeField: projectLocationInputs?.minLatitude != 0 ? projectLocationInputs?.minLatitude : null,
      maxLatitudeField: projectLocationInputs?.maxLatitude != 0 ? projectLocationInputs?.maxLatitude : null,
      minLongitudeField: projectLocationInputs?.minLongitude != 0 ? projectLocationInputs?.minLongitude : null,
      maxLongitudeField: projectLocationInputs?.maxLongitude != 0 ? projectLocationInputs?.maxLongitude : null,
      dateFormat: projectDateFormat,
      timeFormat: projectTimeFormat,
      projectSupportInfo: projectDetail.applicationSupportInfo ? projectDetail.applicationSupportInfo : '',
      projectLogoDisplay: projectDetail.logoAttachment,
      projectLogoInput: projectLogoInput,
      timezone: projectDetail.projectConfiguration.projectTimeZone,
      projectSupportUsers: projectDetail.projectConfiguration.projectSupportUsers,
      isoDocumentPrefix: projectDetail.projectConfiguration.isoDocumentPrefixList,
    };
  }, [projectDetail]);

  return (
    <>
      <GeneralContainerStyled>
        <GeneralHeaderStyled>
          <Text family="graphik" size="500" weight="600" color="white">
            <Trans>General</Trans>
          </Text>
        </GeneralHeaderStyled>

        {(projectLoading || projectNetworkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!projectLoading && projectError && (
          <div data-testid="error-retry">
            <ErrorRetry refetch={projectRefetch}>
              <Trans>There was a problem loading project data. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </div>
        )}
        {!projectLoading && !projectError && !projectDetail && (
          <div data-testid="no-project-data">
            <ErrorRetry refetch={projectRefetch}>
              <Trans>No Project detail found. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </div>
        )}

        {initialValues && !projectLoading && !projectError && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={projectDetailsValidationSchema}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={async (values) => {
              if (!projectDetail || !projectDetail.id) return;
              const supportUser: ProjectSupportUserInput[] = values.projectSupportUsers.map((item) => {
                return {
                  userId: item.id,
                  email: item.email,
                  phone: item.phone,
                  location: item.location,
                };
              });

              const projectLocations: ProjectLocationInput = {
                minLatitude: values?.minLatitudeField ? parseFloat(values?.minLatitudeField) : 0,
                minLongitude: values?.minLongitudeField ? parseFloat(values?.minLongitudeField) : 0,
                maxLatitude: values?.maxLatitudeField ? parseFloat(values?.maxLatitudeField) : 0,
                maxLongitude: values?.maxLongitudeField ? parseFloat(values?.maxLongitudeField) : 0,
              };

              const input: UpdateProjectInput = {
                projectId: projectDetail.id,
                name: values.projectName,
                location: values.city,
                projectlocation: projectLocations,
                stateId: values.state ? values.state.id : '',
                countryId: values.country ? values.country.id : '',
                languageId: values.language ? values.language.id : '',
                applicationSupportInfo: values.projectSupportInfo,
                projectSupportUsers: supportUser,
                logoAttachmentMetadata: values.projectLogoInput,
                timeZoneId: values.timezone.id,
                timeFormatId: values.timeFormat ? values.timeFormat.id : '',
                dateFormatId: values.dateFormat ? values.dateFormat.id : '',
                isoDocumentPrefixList: values?.isoDocumentPrefix ? values?.isoDocumentPrefix : '',
              };
              await updateProject({
                variables: { input },
              });
            }}
          >
            {(form) => {
              const { dirty, isValid, errors } = form;
              return (
                <>
                  <GeneralSubContainerStyled>
                    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                      <GeneralInnerBlockStyled>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          flexGrow={{ sm: '1' }}
                          css={`
                            max-width: 352px;
                          `}
                          pt={{ sm: '600' }}
                          ml={{ sm: '500' }}
                          pb={{ sm: '600' }}
                        >
                          <Box mb={{ sm: '550' }}>
                            <Text family="graphik" size="500" weight="600">
                              <Trans>Project Overview</Trans>
                            </Text>
                          </Box>
                          <Box my={{ sm: '500' }} flexGrow={{ sm: '1' }} data-testid="project-number">
                            <FormControl
                              label={t`Project Number (Required)`}
                              css={`
                                flex-grow: 1;
                              `}
                            >
                              <Field name="projectNumber">
                                {({ field }) => {
                                  return (
                                    <TextInputStyled
                                      disabled={true}
                                      size={TextFieldSize.LARGE}
                                      {...field}
                                      placeholder={t`Search by project number or name`}
                                    />
                                  );
                                }}
                              </Field>
                            </FormControl>
                          </Box>
                          <Box
                            flexDirection={{ sm: 'column' }}
                            my={{ sm: '500' }}
                            flexGrow={{ sm: '1' }}
                            data-testid="project-name"
                          >
                            <FormControl
                              label={t`Project Name (Required)`}
                              css={`
                                flex-grow: 1;
                              `}
                            >
                              <Field name="projectName">
                                {({ field }) => {
                                  return (
                                    <TextInputStyled
                                      size={TextFieldSize.LARGE}
                                      {...field}
                                      placeholder={t`Type your preferred project name`}
                                    />
                                  );
                                }}
                              </Field>
                            </FormControl>
                            <ErrorMessage name="projectName">
                              {(msg) => {
                                return <FieldError msg={i18n._(msg)} />;
                              }}
                            </ErrorMessage>
                          </Box>

                          <Box flexDirection={{ sm: 'column' }} my={{ sm: '500' }} flexGrow={{ sm: '1' }}>
                            <FormControl
                              label={t`ISO Document Prefixes`}
                              css={`
                                flex-grow: 1;
                              `}
                            >
                              <Field name="isoDocumentPrefix">
                                {({ field }) => {
                                  return (
                                    <TextInputStyled
                                      size={TextFieldSize.LARGE}
                                      {...field}
                                      placeholder={t`Enter ISO Document Prefixes`}
                                    />
                                  );
                                }}
                              </Field>
                            </FormControl>
                            <ErrorMessage name="isoDocumentPrefix">
                              {(msg) => {
                                return <FieldError msg={i18n._(msg)} />;
                              }}
                            </ErrorMessage>
                          </Box>

                          <Box mt={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
                            {form.values.projectLogoDisplay && (
                              <>
                                <PhotoContainerStyled>
                                  <Intrinsic ratio={{ sm: '1:1', md: '4:3', lg: '16:9' }}>
                                    <img
                                      src={form.values.projectLogoDisplay.url}
                                      data-testid={`attachment-url-${form.values.projectLogoDisplay.id}`}
                                    />
                                  </Intrinsic>
                                  <DeletePhotoStyled>
                                    <Button
                                      appearance={ButtonAppearance.SECONDARY}
                                      size={ButtonSize.SMALL}
                                      onClick={() => {
                                        setShowDeleteConfirm(true);
                                      }}
                                    >
                                      <Box mr={{ sm: '300' }}>
                                        <Icon icon="trash-alt" fillColor="red.800" />
                                      </Box>
                                      <Trans>Delete</Trans>
                                    </Button>
                                  </DeletePhotoStyled>
                                </PhotoContainerStyled>
                                <DisplayFileDetails logo={form.values.projectLogoDisplay} />
                                {showDeleteConfirm && (
                                  <ErrorBoundary>
                                    <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                                      <LogoDeleteModal
                                        onCancel={() => {
                                          setShowDeleteConfirm(false);
                                        }}
                                        onDelete={() => {
                                          form.setFieldValue('projectLogoDisplay', undefined);
                                          form.setFieldValue('projectLogoInput', undefined);
                                          setShowDeleteConfirm(false);
                                        }}
                                      />
                                    </Suspense>
                                  </ErrorBoundary>
                                )}
                              </>
                            )}
                            {!form.values.projectLogoDisplay && (
                              <FormControl label={form.values?.projectLogoInput ? '' : t`Project Logo`}>
                                <FileUpload
                                  maxFiles={1}
                                  maxFileSizeMB={10}
                                  acceptFileTypes={t`image/jpeg, image/png, image/svg+xml`}
                                  onSuccess={(res: FileUploadResponse, file) => {
                                    const projectLogoInput: AttachmentMetadataInput = {
                                      attachmentId: res.attachmentId,
                                      fileName: file.name,
                                      fileSize: file.size,
                                      mimeType: file.type,
                                    };
                                    form.setFieldValue('projectLogoInput', projectLogoInput);
                                  }}
                                >
                                  {({ getInputProps, getRootProps, loading, files, setFiles, acceptFileTypes }) => {
                                    return (
                                      <Fragment>
                                        {!form.values.projectLogoInput && (
                                          <FileUploadInput
                                            getInputProps={getInputProps}
                                            getRootProps={getRootProps}
                                            loading={loading}
                                            acceptFileTypes={acceptFileTypes}
                                          />
                                        )}
                                        {form.values.projectLogoInput && files && files.length > 0 && (
                                          <>
                                            <ImagePreviewContainerStyled
                                              mb={{ sm: '400' }}
                                              flexDirection={{ sm: 'column' }}
                                            >
                                              <FileUploadPreviewImg
                                                file={files[0]}
                                                onDelete={async () => {
                                                  setFiles([]);
                                                  form.setFieldValue('projectLogoDisplay', undefined);
                                                  form.setFieldValue('projectLogoInput', undefined);
                                                }}
                                              />
                                            </ImagePreviewContainerStyled>
                                            <DisplayFileDetails logo={form.values.projectLogoInput} />
                                          </>
                                        )}
                                      </Fragment>
                                    );
                                  }}
                                </FileUpload>
                              </FormControl>
                            )}
                          </Box>
                        </Box>
                      </GeneralInnerBlockStyled>
                      <GeneralInnerBlockStyled>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          flexGrow={{ sm: '1' }}
                          css={`
                            max-width: 352px;
                          `}
                          pt={{ sm: '600' }}
                          ml={{ sm: '500' }}
                          pb={{ sm: '600' }}
                        >
                          <Box mb={{ sm: '550' }}>
                            <Text family="graphik" size="500" weight="600">
                              <Trans>Language &amp; Region</Trans>
                            </Text>
                          </Box>

                          {!lngLoading && lngError && (
                            <ErrorRetry refetch={lngRefetch}>
                              <Trans>
                                There was a problem loading languages. Please click below button to try again.
                              </Trans>
                            </ErrorRetry>
                          )}

                          {!lngError && (
                            <Box my={{ sm: '500' }} flexGrow={{ sm: '1' }} data-testid="language">
                              <FormControl
                                label={t`Language (Required)`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="language">
                                  {({ field }) => {
                                    return (
                                      <Select
                                        name="language"
                                        disabled={true}
                                        size={TextFieldSize.LARGE}
                                        appearance={TextFieldAppearance.NORMAL}
                                        items={languageList}
                                        selectedItem={field.value}
                                        itemComponent={DropdownItem}
                                        selectedComponent={DropdownSelectedItem}
                                        onSelectedItemChange={(changes: any) => {
                                          form.setFieldValue('language', changes.selectedItem);
                                        }}
                                        loading={lngLoading || lngNetworkStatus === NetworkStatus.refetch}
                                      />
                                    );
                                  }}
                                </Field>
                              </FormControl>
                            </Box>
                          )}

                          {!cntLoading && cntError && (
                            <ErrorRetry refetch={cntRefetch}>
                              <Trans>
                                There was a problem loading countries. Please click below button to try again.
                              </Trans>
                            </ErrorRetry>
                          )}
                          {!cntError && (
                            <Box
                              flexDirection={{ sm: 'column' }}
                              my={{ sm: '500' }}
                              flexGrow={{ sm: '1' }}
                              data-testid="country"
                            >
                              <FormControl
                                label={t`Country (Required)`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="country">
                                  {({ field }) => {
                                    return (
                                      <Select
                                        name="country"
                                        size={TextFieldSize.LARGE}
                                        appearance={TextFieldAppearance.NORMAL}
                                        items={countryList}
                                        selectedItem={field.value}
                                        itemComponent={DropdownItem}
                                        selectedComponent={DropdownSelectedItem}
                                        onSelectedItemChange={(changes: any) => {
                                          form.setFieldValue('country', changes.selectedItem);
                                          if (changes.selectedItem.id === initialValues?.country?.id)
                                            form.setFieldValue('state', initialValues?.state);
                                          else form.setFieldValue('state', null);
                                          setCountry(changes.selectedItem);
                                        }}
                                        loading={cntLoading || cntNetworkStatus === NetworkStatus.refetch}
                                      />
                                    );
                                  }}
                                </Field>
                                {errors && errors.country && <FieldError msg={i18n._(errors?.country)} />}
                              </FormControl>
                            </Box>
                          )}

                          {!stLoading && stError && (
                            <ErrorRetry refetch={stRefetch}>
                              <Trans>There was a problem loading states. Please click below button to try again.</Trans>
                            </ErrorRetry>
                          )}
                          {!stError && (
                            <Box
                              flexDirection={{ sm: 'column' }}
                              my={{ sm: '500' }}
                              flexGrow={{ sm: '1' }}
                              data-testid="state"
                            >
                              <FormControl
                                label={t`State/Province/Region (Required)`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="state">
                                  {({ field }) => {
                                    return (
                                      <Select
                                        name="state"
                                        size={TextFieldSize.LARGE}
                                        appearance={TextFieldAppearance.NORMAL}
                                        items={stateList}
                                        selectedItem={field.value}
                                        itemComponent={DropdownItem}
                                        selectedComponent={DropdownSelectedItem}
                                        onSelectedItemChange={(changes: any) => {
                                          form.setFieldValue('state', changes.selectedItem);
                                        }}
                                        loading={stLoading || stNetworkStatus === NetworkStatus.refetch}
                                      />
                                    );
                                  }}
                                </Field>
                                {errors && errors.state && <FieldError msg={i18n._(errors?.state)} />}
                              </FormControl>
                            </Box>
                          )}

                          <Box
                            flexDirection={{ sm: 'column' }}
                            my={{ sm: '500' }}
                            flexGrow={{ sm: '1' }}
                            data-testid="city"
                          >
                            <FormControl
                              label={t`City/Town`}
                              css={`
                                flex-grow: 1;
                              `}
                            >
                              <Field name="city">
                                {({ field }) => {
                                  return (
                                    <TextInputStyled size={TextFieldSize.LARGE} {...field} placeholder={t`City`} />
                                  );
                                }}
                              </Field>
                            </FormControl>
                            <ErrorMessage name="city">
                              {(msg) => {
                                return <FieldError msg={i18n._(msg)} />;
                              }}
                            </ErrorMessage>
                          </Box>
                        </Box>
                      </GeneralInnerBlockStyled>
                      <GeneralInnerBlockStyled>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          flexGrow={{ sm: '1' }}
                          css={`
                            max-width: 352px;
                          `}
                          pt={{ sm: '600' }}
                          ml={{ sm: '500' }}
                          pb={{ sm: '600' }}
                        >
                          <Box mb={{ sm: '550' }}>
                            <Text family="graphik" size="500" weight="600">
                              <Trans>Location</Trans>
                            </Text>
                          </Box>

                          <Box
                            flexDirection={{ sm: 'row' }}
                            my={{ sm: '500' }}
                            mr={{ sm: '500' }}
                            flexGrow={{ sm: '1' }}
                          >
                            <Box
                              mr={{ sm: '500' }}
                              style={{ minWidth: `${widthLatitude}` }}
                              data-testid="min-latitude-field"
                            >
                              <FormControl
                                label={t`Min Latitude`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="minLatitudeField">
                                  {({ field }) => {
                                    return (
                                      <TextInputStyled
                                        size={TextFieldSize.LARGE}
                                        {...field}
                                        onKeyPress={(event) => {
                                          if (!/^-?[0-9]*\.?[0-9]*$/.test(event.key)) {
                                            event.preventDefault();
                                          }
                                        }}
                                        placeholder={t`Min Latitude`}
                                      />
                                    );
                                  }}
                                </Field>
                              </FormControl>
                            </Box>
                            <Box data-testid="max-latitude-field" style={{ minWidth: `${widthLatitude}` }}>
                              <FormControl
                                label={t`Max Latitude`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="maxLatitudeField">
                                  {({ field }) => {
                                    return (
                                      <TextInputStyled
                                        size={TextFieldSize.LARGE}
                                        {...field}
                                        onKeyPress={(event) => {
                                          if (!/^-?[0-9]*\.?[0-9]*$/.test(event.key)) {
                                            event.preventDefault();
                                          }
                                        }}
                                        placeholder={t`Max Latitude`}
                                      />
                                    );
                                  }}
                                </Field>
                              </FormControl>
                            </Box>
                          </Box>
                          <Box flexDirection={{ sm: 'row' }} my={{ sm: '500' }} flexGrow={{ sm: '1' }}>
                            <Box mr={{ sm: '500' }} data-testid="min-longitude-field">
                              <FormControl
                                label={t`Min Longitude`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="minLongitudeField">
                                  {({ field }) => {
                                    return (
                                      <TextInputStyled
                                        size={TextFieldSize.LARGE}
                                        {...field}
                                        onKeyPress={(event) => {
                                          if (!/^-?[0-9]*\.?[0-9]*$/.test(event.key)) {
                                            event.preventDefault();
                                          }
                                        }}
                                        placeholder={t`Min Longitude`}
                                      />
                                    );
                                  }}
                                </Field>
                              </FormControl>
                            </Box>
                            <Box data-testid="max-longitude-field">
                              <FormControl
                                label={t`Max Longitude`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="maxLongitudeField">
                                  {({ field }) => {
                                    return (
                                      <TextInputStyled
                                        size={TextFieldSize.LARGE}
                                        {...field}
                                        onKeyPress={(event) => {
                                          if (!/^-?[0-9]*\.?[0-9]*$/.test(event.key)) {
                                            event.preventDefault();
                                          }
                                        }}
                                        placeholder={t`Max Longitude`}
                                      />
                                    );
                                  }}
                                </Field>
                              </FormControl>
                            </Box>
                          </Box>
                          {features?.showMapView && <ProjectMapLocation />}
                        </Box>
                      </GeneralInnerBlockStyled>
                      <GeneralInnerBlockStyled>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          flexGrow={{ sm: '1' }}
                          css={`
                            max-width: 352px;
                          `}
                          pt={{ sm: '600' }}
                          ml={{ sm: '500' }}
                          pb={{ sm: '600' }}
                        >
                          <Box mb={{ sm: '550' }}>
                            <Text family="graphik" size="500" weight="600">
                              <Trans>Date &amp; Time</Trans>
                            </Text>
                          </Box>

                          {!dateFormatLoading && dateFormatError && (
                            <ErrorRetry refetch={dateFormatRefetch}>
                              <Trans>
                                There was a problem loading date formats. Please click below button to try again.
                              </Trans>
                            </ErrorRetry>
                          )}
                          {!dateFormatError && (
                            <Box
                              flexDirection={{ sm: 'column' }}
                              my={{ sm: '500' }}
                              flexGrow={{ sm: '1' }}
                              data-testid="date-format"
                            >
                              <FormControl
                                label={t`Date Format (Required)`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="dateFormat">
                                  {({ field }) => {
                                    return (
                                      <Select
                                        name="dateFormat"
                                        size={TextFieldSize.LARGE}
                                        appearance={TextFieldAppearance.NORMAL}
                                        items={dateFormatList}
                                        selectedItem={field.value}
                                        itemComponent={DateFormatItem}
                                        selectedComponent={DateFormatSelectedItem}
                                        onSelectedItemChange={(changes: any) => {
                                          form.setFieldValue('dateFormat', changes.selectedItem);
                                        }}
                                        loading={dateFormatLoading || dateFormatNetworkStatus === NetworkStatus.refetch}
                                      />
                                    );
                                  }}
                                </Field>
                                {errors && errors.dateFormat && <FieldError msg={i18n._(errors?.dateFormat)} />}
                              </FormControl>
                            </Box>
                          )}
                          {!timeFormatLoading && timeFormatError && (
                            <ErrorRetry refetch={timeFormatRefetch}>
                              <Trans>
                                There was a problem loading time formats. Please click below button to try again.
                              </Trans>
                            </ErrorRetry>
                          )}
                          {!timeFormatError && (
                            <Box
                              flexDirection={{ sm: 'column' }}
                              my={{ sm: '500' }}
                              flexGrow={{ sm: '1' }}
                              data-testid="time-format"
                            >
                              <FormControl
                                label={t`Time Format (Required)`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="timeFormat">
                                  {({ field }) => {
                                    return (
                                      <Select
                                        name="timeFormat"
                                        size={TextFieldSize.LARGE}
                                        appearance={TextFieldAppearance.NORMAL}
                                        items={timeFormatList}
                                        selectedItem={field.value}
                                        itemComponent={TimeFormatItem}
                                        selectedComponent={TimeFormatSelectedItem}
                                        onSelectedItemChange={(changes: any) => {
                                          form.setFieldValue('timeFormat', changes.selectedItem);
                                        }}
                                        loading={timeFormatLoading || timeFormatNetworkStatus === NetworkStatus.refetch}
                                      />
                                    );
                                  }}
                                </Field>
                                {errors && errors.timeFormat && <FieldError msg={i18n._(errors?.timeFormat)} />}
                              </FormControl>
                            </Box>
                          )}

                          {!timezoneLoading && timezoneError && (
                            <ErrorRetry refetch={timezoneRefetch}>
                              <Trans>
                                There was a problem loading timezones. Please click below button to try again.
                              </Trans>
                            </ErrorRetry>
                          )}

                          {!timezoneError && (
                            <Box my={{ sm: '500' }} flexGrow={{ sm: '1' }} data-testid="timezone">
                              <FormControl
                                label={t`Time Zone (Required)`}
                                css={`
                                  flex-grow: 1;
                                `}
                              >
                                <Field name="timezone">
                                  {({ field }) => {
                                    return (
                                      <Select
                                        name="timezone"
                                        size={TextFieldSize.LARGE}
                                        appearance={TextFieldAppearance.NORMAL}
                                        items={timezoneList}
                                        selectedItem={field.value}
                                        itemComponent={TimeZoneItem}
                                        selectedComponent={TimeZoneSelectedItem}
                                        onSelectedItemChange={(changes: any) => {
                                          form.setFieldValue('timezone', changes.selectedItem);
                                        }}
                                        loading={timezoneLoading || timezoneNetworkStatus === NetworkStatus.refetch}
                                      />
                                    );
                                  }}
                                </Field>
                                {errors && errors.timezone && <FieldError msg={i18n._(errors?.timezone)} />}
                              </FormControl>
                            </Box>
                          )}
                        </Box>
                      </GeneralInnerBlockStyled>
                      <GeneralInnerBlockStyled>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          flexGrow={{ sm: '1' }}
                          css={`
                            max-width: 366px;
                          `}
                          pt={{ sm: '600' }}
                          ml={{ sm: '500' }}
                          pb={{ sm: '600' }}
                        >
                          <Box mb={{ sm: '500' }}>
                            <Text family="graphik" size="500" weight="600">
                              {t`${isPFAppType ? 'PipeFighter' : 'ArcStryker'} Application Support`}
                            </Text>
                          </Box>
                          <Box mb={{ sm: '500' }}>
                            <Text
                              family="graphik"
                              size="300"
                              color="grey.1000"
                              style={{ lineHeight: `${theme.spacings[550]}` }}
                            >
                              <Trans>
                                Tell your users where to go for project support. This contact information will be
                                displayed in the
                              </Trans>
                              &nbsp;
                              <Text style={{ textDecoration: 'underline' }}>
                                <Trans>personal support settings</Trans>
                              </Text>{' '}
                              {t`window for all ${isPFAppType ? 'PipeFighter' : 'ArcStryker'} users on your project.`}
                            </Text>
                          </Box>
                          <Box mb={{ sm: '300' }} flexGrow={{ sm: '1' }} data-testid="project-support-info">
                            <FormControl
                              label=""
                              css={`
                                flex-grow: 1;
                              `}
                            >
                              <Field name="projectSupportInfo">
                                {({ field }) => {
                                  return (
                                    <TextAreaStyled
                                      size={TextFieldSize.LARGE}
                                      {...field}
                                      placeholder={t`How should your users contact project support?`}
                                      rows="6"
                                    />
                                  );
                                }}
                              </Field>
                            </FormControl>
                          </Box>

                          <Box mb={{ sm: '550' }}>
                            <Text
                              family="graphik"
                              size="300"
                              color="grey.1000"
                              style={{ lineHeight: `${theme.spacings[550]}` }}
                            >
                              <Text weight="600">
                                <Trans>Hint</Trans>:
                              </Text>{' '}
                              <Trans>
                                This could be a PTT channel or a Super User’s phone number, or other contact
                                information. Double-check for typos.
                              </Trans>
                            </Text>
                          </Box>
                        </Box>
                      </GeneralInnerBlockStyled>
                      <GeneralInnerBlockStyled data-testid={`project-support-users`}>
                        <Field name="projectSupportUsers">
                          {({ field }) => {
                            return (
                              <>
                                {field.value &&
                                  field.value.map((item: ProjectSupportUser, index: number) => {
                                    return <UserItem key={index} item={item} index={index} form={form} />;
                                  })}
                              </>
                            );
                          }}
                        </Field>
                      </GeneralInnerBlockStyled>
                    </Box>
                  </GeneralSubContainerStyled>
                  {!projectLoading && !projectError && (
                    <FooterButtonStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <GeneralFooterButtonStyled
                            disabled={updateProjectLoading || !dirty}
                            onClick={() => {
                              form.handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </GeneralFooterButtonStyled>
                        </Box>
                        <GeneralFooterButtonStyled
                          data-testid={`project-save`}
                          onClick={() => {
                            form.handleSubmit();
                          }}
                          disabled={updateProjectLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {updateProjectLoading && <Spinner />}
                          {!updateProjectLoading && t`Save Changes`}
                        </GeneralFooterButtonStyled>
                      </Box>
                    </FooterButtonStyled>
                  )}
                </>
              );
            }}
          </Formik>
        )}
      </GeneralContainerStyled>
      {updateProjectRequestError && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <UpdateProjectRequestErrorModal updateProjectError={updateProjectRequestError} close={closeModal} />
        </Suspense>
      )}
    </>
  );
};
